import { BadgeVenta, VENTA_META } from '@/components/BadgeVenta';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useObras, useSetEstadoObra } from '@/hooks/useObras';
import { useProyectos } from '@/hooks/useProyectos';
import type { EstadoObra, EstadoVenta, Obra, TipoCasa } from '@adelante/shared';
import {
  ArrowClockwise,
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  CircleNotch,
  Gear,
  Lightning,
  Prohibit,
} from '@phosphor-icons/react';
import { type ReactNode, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfigurarObraDialog } from './admin/ConfigurarObraDialog';
import { HabilitarLoteDialog } from './admin/HabilitarLoteDialog';

/** ₡ sin decimales para esta tabla (montos grandes de presupuesto). */
function crc0(n: number): string {
  return `₡${Math.round(n).toLocaleString('es-CR')}`;
}

const TIPOS_CASA: { value: TipoCasa; label: string }[] = [
  { value: '1N-Techo', label: '1 Nivel — Techo' },
  { value: '1N-Azotea', label: '1 Nivel — Azotea' },
  { value: '2N-Techo', label: '2 Niveles — Techo' },
  { value: '2N-Azotea', label: '2 Niveles — Azotea' },
];

const TODOS = '__todos__';

const ESTADO_LABEL: Record<EstadoObra, string> = {
  pendiente: 'Pendiente',
  en_ejecucion: 'En ejecución',
  en_espera: 'En espera',
  inactiva: 'Inactiva',
  finalizada: 'Finalizada',
};

// --- Orden + filtro por columna ----------------------------------------------

type ColKey =
  | 'codigo'
  | 'proyecto'
  | 'bloque'
  | 'modelo'
  | 'tipo'
  | 'm2'
  | 'costo'
  | 'indirecto'
  | 'venta_monto'
  | 'venta'
  | 'estado';
type Orden = { col: ColKey; dir: 'asc' | 'desc' } | null;

/** Valor de una obra para una columna (para ordenar y filtrar). */
function valorColumna(o: Obra, col: ColKey): string | number {
  switch (col) {
    case 'codigo':
      return o.codigo;
    case 'proyecto':
      return o.proyecto_codigo ?? '';
    case 'bloque':
      return o.bloque_letra ?? '';
    case 'modelo':
      return o.nombre_modelo ?? '';
    case 'tipo':
      return o.tipo_casa_config ?? o.tipo_casa ?? '';
    case 'm2':
      return o.area_prorrateada ?? 0;
    case 'costo':
      return o.costo_directo ?? 0;
    case 'indirecto':
      return o.presupuesto_indirecto ?? 0;
    case 'venta_monto':
      return o.monto_venta ?? 0;
    case 'venta':
      return o.estado_venta ? VENTA_META[o.estado_venta].label : '';
    case 'estado':
      return o.estado_operativo ? ESTADO_LABEL[o.estado_operativo] : 'No habilitada';
  }
}

function comparar(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'es', { numeric: true });
}

/** Encabezado de columna clickeable para ordenar (asc ⇄ desc). */
function CabezaOrden({
  col,
  orden,
  onOrdenar,
  children,
  className,
}: {
  col: ColKey;
  orden: Orden;
  onOrdenar: (col: ColKey) => void;
  children: ReactNode;
  className?: string;
}) {
  const activo = orden?.col === col;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onOrdenar(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {children}
        {activo ? (
          orden?.dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" weight="bold" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" weight="bold" aria-hidden="true" />
          )
        ) : (
          <ArrowsDownUp className="h-3 w-3 opacity-30" weight="bold" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );
}

/** Celda de filtro de texto para una columna. */
function FiltroCol({
  col,
  valor,
  onCambio,
}: {
  col: ColKey;
  valor: string;
  onCambio: (col: ColKey, v: string) => void;
}) {
  return (
    <TableHead className="py-1">
      <Input
        value={valor}
        onChange={(e) => onCambio(col, e.target.value)}
        placeholder="Filtrar…"
        className="h-7 text-xs"
      />
    </TableHead>
  );
}

function EstadoOperativoBadge({ obra }: { obra: Obra }) {
  if (obra.estado_operativo == null) {
    return <span className="text-xs text-muted-foreground">No habilitada</span>;
  }
  const esEjecucion = obra.estado_operativo === 'en_ejecucion';
  return (
    <span className="flex flex-col gap-0.5">
      <Badge
        variant={esEjecucion ? 'default' : 'secondary'}
        className={esEjecucion ? 'bg-adelante-primary text-adelante-foreground' : ''}
      >
        {ESTADO_LABEL[obra.estado_operativo]}
      </Badge>
      {obra.sprint_actual != null && (
        <span className="text-[11px] text-muted-foreground">S{obra.sprint_actual}</span>
      )}
    </span>
  );
}

export function ObrasPantalla() {
  const [proyecto, setProyecto] = useState<string>(TODOS);
  const [tipoCasa, setTipoCasa] = useState<string>(TODOS);
  const [configurando, setConfigurando] = useState<Obra | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [loteAbierto, setLoteAbierto] = useState(false);
  const setEstado = useSetEstadoObra();

  // Inhabilitar (inactiva → desaparece del tablero) / Reactivar (en ejecución,
  // mantiene el sprint). Directo, sin escoger sprint.
  function cambiarEstado(o: Obra, estado: 'inactiva' | 'en_ejecucion') {
    const inhab = estado === 'inactiva';
    setEstado.mutate(
      { codigo: o.codigo, estado, motivo: inhab ? 'Inhabilitada manualmente' : null },
      {
        onSuccess: () =>
          toast.success(`${o.codigo} ${inhab ? 'inhabilitada' : 'reactivada'}`, {
            description: inhab
              ? 'Sale del tablero de avance.'
              : `Vuelve al tablero en el sprint ${o.sprint_actual ?? 1}.`,
          }),
        onError: (e) =>
          toast.error('No se pudo cambiar el estado', {
            description: e instanceof Error ? e.message : 'Error',
          }),
      },
    );
  }

  const proyectosQuery = useProyectos();

  const filtros = {
    ...(proyecto !== TODOS ? { proyecto } : {}),
    ...(tipoCasa !== TODOS ? { tipo_casa: tipoCasa as TipoCasa } : {}),
  };

  const obrasQuery = useObras(filtros);
  // vw_obras puede traer códigos duplicados (dim_obra con varias filas por
  // works_no). Deduplicamos por código para que el conteo, la selección y el
  // filtrado por columna funcionen bien (la key de React debe ser única).
  const obras = useMemo(() => {
    const raw = obrasQuery.data?.data ?? [];
    const vistos = new Set<string>();
    const out: Obra[] = [];
    for (const o of raw) {
      if (!vistos.has(o.codigo)) {
        vistos.add(o.codigo);
        out.push(o);
      }
    }
    return out;
  }, [obrasQuery.data]);

  // Filtros rápidos por estado operativo. Por defecto OCULTA las finalizadas
  // (para ver lo que está en juego); las no habilitadas se muestran.
  const [excluirFin, setExcluirFin] = useState(true);
  const [excluirNoHab, setExcluirNoHab] = useState(false);

  // Orden + filtros por columna (client-side sobre lo ya cargado).
  const [orden, setOrden] = useState<Orden>(null);
  const [colFiltros, setColFiltros] = useState<Partial<Record<ColKey, string>>>({});
  const setFiltro = (col: ColKey, val: string) =>
    setColFiltros((prev) => ({ ...prev, [col]: val }));
  function ordenarPor(col: ColKey) {
    setOrden((prev) =>
      prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
    );
  }

  const vista = useMemo(() => {
    let lista = obras.filter((o) => {
      if (excluirFin && o.estado_operativo === 'finalizada') return false;
      if (excluirNoHab && o.estado_operativo == null) return false;
      for (const [col, val] of Object.entries(colFiltros)) {
        if (!val) continue;
        if (
          !String(valorColumna(o, col as ColKey))
            .toLowerCase()
            .includes(val.toLowerCase())
        )
          return false;
      }
      return true;
    });
    if (orden) {
      const dir = orden.dir === 'asc' ? 1 : -1;
      lista = [...lista].sort(
        (a, b) => dir * comparar(valorColumna(a, orden.col), valorColumna(b, orden.col)),
      );
    }
    return lista;
  }, [obras, colFiltros, orden, excluirFin, excluirNoHab]);

  const todasVisiblesSel = vista.length > 0 && vista.every((o) => seleccion.has(o.codigo));

  function toggleUno(codigo: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  }

  function toggleTodasVisibles() {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (todasVisiblesSel) {
        for (const o of vista) next.delete(o.codigo);
      } else {
        for (const o of vista) next.add(o.codigo);
      }
      return next;
    });
  }

  const kpis = useMemo(() => {
    const venta: Record<EstadoVenta, number> = {
      entregada: 0,
      formalizada: 0,
      reservada: 0,
      disponible: 0,
    };
    for (const o of obras) {
      if (o.estado_venta) venta[o.estado_venta]++;
    }
    return { total: obras.length, venta };
  }, [obras]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Obras</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Listado de obras vigentes desde AdelanteDB (vista <code>obc.vw_obras</code>).
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label
              htmlFor="filtro-proyecto"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Proyecto
            </Label>
            <Select value={proyecto} onValueChange={setProyecto}>
              <SelectTrigger id="filtro-proyecto">
                <SelectValue placeholder="Todos los proyectos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {proyectosQuery.data?.data?.map((p) => (
                  <SelectItem key={p.codigo} value={p.codigo}>
                    {p.codigo} — {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="filtro-tipo-casa"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Tipo de casa
            </Label>
            <Select value={tipoCasa} onValueChange={setTipoCasa}>
              <SelectTrigger id="filtro-tipo-casa">
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {TIPOS_CASA.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block text-xs font-medium text-muted-foreground">Mostrar</Label>
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="excl-fin"
                  checked={excluirFin}
                  onCheckedChange={(v) => setExcluirFin(v === true)}
                />
                <Label htmlFor="excl-fin" className="cursor-pointer font-normal">
                  Excluir finalizadas
                </Label>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="excl-nohab"
                  checked={excluirNoHab}
                  onCheckedChange={(v) => setExcluirNoHab(v === true)}
                />
                <Label htmlFor="excl-nohab" className="cursor-pointer font-normal">
                  Excluir no habilitadas
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs por estado de venta */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {(['entregada', 'formalizada', 'reservada', 'disponible'] as const).map((ev) => {
          const m = VENTA_META[ev];
          return (
            <Card key={ev}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${m.dot}`}
                    aria-hidden="true"
                  />
                  Obras {m.label.toLowerCase()}s
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{kpis.venta[ev]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabla */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="flex items-baseline gap-2">
            Detalle de obras
            <span className="text-sm font-normal text-muted-foreground">
              {vista.length === obras.length ? obras.length : `${vista.length} de ${obras.length}`}
            </span>
          </CardTitle>
          {seleccion.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {seleccion.size} seleccionada{seleccion.size === 1 ? '' : 's'}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setSeleccion(new Set())}>
                Limpiar
              </Button>
              <Button size="sm" onClick={() => setLoteAbierto(true)}>
                <Lightning className="h-4 w-4" weight="bold" aria-hidden="true" />
                Habilitar {seleccion.size}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {obrasQuery.isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando obras…
            </div>
          )}

          {obrasQuery.error && (
            <p className="py-8 text-destructive">Error al cargar: {obrasQuery.error.message}</p>
          )}

          {!obrasQuery.isLoading && !obrasQuery.error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={todasVisiblesSel}
                      onCheckedChange={toggleTodasVisibles}
                      aria-label="Seleccionar todas las visibles"
                    />
                  </TableHead>
                  <CabezaOrden col="codigo" orden={orden} onOrdenar={ordenarPor}>
                    Código
                  </CabezaOrden>
                  <CabezaOrden col="proyecto" orden={orden} onOrdenar={ordenarPor}>
                    Proyecto
                  </CabezaOrden>
                  <CabezaOrden col="bloque" orden={orden} onOrdenar={ordenarPor} className="w-12">
                    Bloque
                  </CabezaOrden>
                  <CabezaOrden col="modelo" orden={orden} onOrdenar={ordenarPor}>
                    Modelo
                  </CabezaOrden>
                  <CabezaOrden col="tipo" orden={orden} onOrdenar={ordenarPor}>
                    Tipo
                  </CabezaOrden>
                  <CabezaOrden col="m2" orden={orden} onOrdenar={ordenarPor} className="text-right">
                    m²
                  </CabezaOrden>
                  <CabezaOrden
                    col="costo"
                    orden={orden}
                    onOrdenar={ordenarPor}
                    className="text-right"
                  >
                    Costo Directo
                  </CabezaOrden>
                  <CabezaOrden
                    col="indirecto"
                    orden={orden}
                    onOrdenar={ordenarPor}
                    className="text-right"
                  >
                    Indirecto
                  </CabezaOrden>
                  <CabezaOrden
                    col="venta_monto"
                    orden={orden}
                    onOrdenar={ordenarPor}
                    className="text-right"
                  >
                    Venta
                  </CabezaOrden>
                  <CabezaOrden col="venta" orden={orden} onOrdenar={ordenarPor}>
                    Edo. venta
                  </CabezaOrden>
                  <CabezaOrden col="estado" orden={orden} onOrdenar={ordenarPor}>
                    Estado captura
                  </CabezaOrden>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10 py-1" />
                  <FiltroCol col="codigo" valor={colFiltros.codigo ?? ''} onCambio={setFiltro} />
                  <FiltroCol
                    col="proyecto"
                    valor={colFiltros.proyecto ?? ''}
                    onCambio={setFiltro}
                  />
                  <FiltroCol col="bloque" valor={colFiltros.bloque ?? ''} onCambio={setFiltro} />
                  <FiltroCol col="modelo" valor={colFiltros.modelo ?? ''} onCambio={setFiltro} />
                  <FiltroCol col="tipo" valor={colFiltros.tipo ?? ''} onCambio={setFiltro} />
                  <TableHead className="py-1" />
                  <TableHead className="py-1" />
                  <TableHead className="py-1" />
                  <TableHead className="py-1" />
                  <FiltroCol col="venta" valor={colFiltros.venta ?? ''} onCambio={setFiltro} />
                  <FiltroCol col="estado" valor={colFiltros.estado ?? ''} onCambio={setFiltro} />
                  <TableHead className="py-1" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {vista.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} className="py-8 text-center text-muted-foreground">
                      No hay obras con los filtros actuales.
                    </TableCell>
                  </TableRow>
                )}
                {vista.map((o) => (
                  <TableRow
                    key={o.codigo}
                    data-state={seleccion.has(o.codigo) ? 'selected' : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        checked={seleccion.has(o.codigo)}
                        onCheckedChange={() => toggleUno(o.codigo)}
                        aria-label={`Seleccionar ${o.codigo}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{o.codigo}</TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={o.proyecto_nombre ?? undefined}
                    >
                      {o.proyecto_codigo}
                    </TableCell>
                    <TableCell className="w-12 text-center">{o.bloque_letra}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {o.nombre_modelo ?? '—'}
                    </TableCell>
                    <TableCell>
                      {(o.tipo_casa_config ?? o.tipo_casa) ? (
                        <Badge variant="outline" className="font-mono text-xs">
                          {o.tipo_casa_config ?? o.tipo_casa}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm">
                      {o.area_prorrateada ? o.area_prorrateada.toFixed(2) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs">
                      {o.costo_directo ? crc0(o.costo_directo) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs text-muted-foreground">
                      {o.presupuesto_indirecto ? crc0(o.presupuesto_indirecto) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs">
                      {o.monto_venta ? crc0(o.monto_venta) : '—'}
                    </TableCell>
                    <TableCell>
                      {o.estado_venta ? (
                        <BadgeVenta estado={o.estado_venta} />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <EstadoOperativoBadge obra={o} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant={o.estado_operativo == null ? 'default' : 'outline'}
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setConfigurando(o)}
                          title={o.estado_operativo == null ? 'Habilitar obra' : 'Configurar obra'}
                        >
                          <Gear className="h-4 w-4" weight="bold" aria-hidden="true" />
                          <span className="sr-only">
                            {o.estado_operativo == null ? 'Habilitar' : 'Configurar'}
                          </span>
                        </Button>
                        {(o.estado_operativo === 'en_ejecucion' ||
                          o.estado_operativo === 'en_espera' ||
                          o.estado_operativo === 'pendiente') && (
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={setEstado.isPending}
                            onClick={() => cambiarEstado(o, 'inactiva')}
                            title="Inhabilitar — sale del tablero de avance (≠ congelar)"
                            className="h-8 w-8 text-red-700 hover:bg-red-50 hover:text-red-800"
                          >
                            <Prohibit className="h-4 w-4" weight="bold" aria-hidden="true" />
                            <span className="sr-only">Inhabilitar</span>
                          </Button>
                        )}
                        {o.estado_operativo === 'inactiva' && (
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={setEstado.isPending}
                            onClick={() => cambiarEstado(o, 'en_ejecucion')}
                            title="Reactivar — vuelve al tablero en su sprint actual"
                            className="h-8 w-8 text-adelante-primary-dark hover:bg-adelante-primary/10"
                          >
                            <ArrowClockwise className="h-4 w-4" weight="bold" aria-hidden="true" />
                            <span className="sr-only">Reactivar</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfigurarObraDialog obra={configurando} onClose={() => setConfigurando(null)} />
      <HabilitarLoteDialog
        codigos={[...seleccion]}
        abierto={loteAbierto}
        onClose={() => setLoteAbierto(false)}
        onHecho={() => setSeleccion(new Set())}
      />
    </div>
  );
}
